import React, { useEffect, useState } from 'react';
import { Dialog } from '@headlessui/react';
import BookService from '../../services/BookService';
import { useNavigate } from 'react-router-dom';
import schoolServiceAdminInstance from '../../services/SchoolServiceAdmin';
import TeacherClass from '../AfterLoginPages/TeacherClass';
import LogsDisplay from '../AfterLoginPages/LogsDisplay';

const BooksServices = ({ data }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [selectedTeacher, setSelectedTeacher] = useState(null);

  const [allBooks, setAllBooks] = useState([]); // Books state
  const [allTeachers, setAllTeachers] = useState(null);
  const [studentsByTeacher, setStudentsByTeacher] = useState({});

  const [isTeacherListVisible, setIsTeacherListVisible] =
    useState(false);

  const [isBooksTableVisible, setIsBooksTableVisible] =
    useState(false); // Books table toggle
  const [newBook, setNewBook] = useState({
    title: '',
    isbn: '',
    totalCopies: 0,
    price: 0,
  });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editBook, setEditBook] = useState(null); // For editing a book
  const [isEditModalOpen, setIsEditModalOpen] = useState(false); // Edit modal visibility

  const [isIssueBookModalOpen, setIsIssueBookModalOpen] =
    useState(false);

  const [selectedBookIds, setSelectedBookIds] = useState([]); // Track selected books for the teacher
  const [bookQuantities, setBookQuantities] = useState({}); // Track quantities for each book

  // State for showing/hiding Books Email Notification Service
  const [isBooksEmailVisible, setIsBooksEmailVisible] =
    useState(false);

  // Check if user is a SUPER_ADMIN
  const isSuperAdmin = data.roles?.some(
    (role) => role.roleCode === 'SUPER_ADMIN',
  );

  useEffect(() => {
    window.scrollTo(0, 0);

    if (!data || !isSuperAdmin) {
      localStorage.clear();
      navigate('/');
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      try {
        const [teachers, books] = await Promise.all([
          schoolServiceAdminInstance.getAllTeachers(),
          BookService.getAllBooks(), // Fetch all books
        ]);

        setAllTeachers(teachers.data);
        setAllBooks(books.data); // Set books

        const studentResponses = await Promise.all(
          teachers.data.map((teacher) =>
            schoolServiceAdminInstance.getAllStudentsForTeacher(
              teacher.id,
            ),
          ),
        );

        const grouped = {};
        teachers.data.forEach((teacher, index) => {
          grouped[teacher.id] = studentResponses[index].data;
        });

        setStudentsByTeacher(grouped);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
      setLoading(false);
    };
    fetchData();
  }, [data, isSuperAdmin, navigate]);

  // Refresh books
  const refreshBooks = async () => {
    setLoading(true);
    try {
      const booksResponse = await BookService.getAllBooks();
      setAllBooks(booksResponse.data);
    } catch (error) {
      console.error('Error refreshing books:', error);
    }
    setLoading(false);
  };

  const toggleBooksTableVisibility = () => {
    setIsBooksTableVisible(!isBooksTableVisible);
  };

  const handleAddBook = async () => {
    try {
      await BookService.createBook(newBook);
      setNewBook({ title: '', isbn: '' });
      refreshBooks();
      setIsModalOpen(false);
      alert('Book added successfully.');
    } catch (error) {
      console.error('Error adding book:', error);
    }
  };

  const openEditModal = (book) => {
    setEditBook({
      ...book,
      status: book.status || 'UPDATED',
    });
    setIsEditModalOpen(true);
  };

  const handleUpdateBook = async () => {
    try {
      // Construct the query string with the status parameter

      await BookService.updateBook(
        editBook.id,
        editBook,
        editBook.status,
      );
      refreshBooks(); // Refresh book list after update
      setIsEditModalOpen(false);
      alert('Book updated successfully.');
    } catch (error) {
      console.error('Error updating book:', error);
    }
  };

  const openIssueBookModal = (teacher) => {
    setSelectedTeacher(teacher);
    setSelectedBookIds([]);
    setBookQuantities({});
    setIsIssueBookModalOpen(true);
  };

  const handleIssueBooksToTeacher = async () => {
    try {
      const issueDetails = selectedBookIds.map((bookId) => ({
        bookId,
        quantity: bookQuantities[bookId] || 0,
      }));

      await BookService.issueBooksToTeacher(
        selectedTeacher.id,
        issueDetails,
      ); // Backend API call
      refreshBooks(); // Refresh book list to update available copies
      setIsIssueBookModalOpen(false);
      alert(
        `Books issued to ${selectedTeacher.firstName} successfully.`,
      );
    } catch (error) {
      console.error('Error issuing books:', error);
      alert('Failed to issue books.');
    }
  };

  return (
    <div className="container mx-auto my-8">
      <div className="container mx-auto my-8">
        <div className="inline-flex items-center justify-center w-full">
          <h1 className="md:text-2xl text-2xl text-center py-2 font-semibold text-cyan-900">
            Books System Logs
          </h1>
        </div>
        <LogsDisplay /> {/* Include the LogsDisplay component */}
      </div>
      <hr className="w-960 h-2  mx-auto my-4 bg-cyan-800 border-0 rounded md:my-10 dark:bg-gray-700" />

      <div className="inline-flex items-center justify-center w-full">
        <h1 className="md:text-2xl text-2xl text-center py-2 font-semibold text-cyan-900 ">
          Books CRUD Service
        </h1>
      </div>
      {/* Book Table Toggle */}
      <div className="inline-flex items-center justify-center w-full">
        <hr className="w-96 h-1 my-8 bg-cyan-800 border-0 rounded" />
        <div className="absolute px-4 -translate-x-1/2 bg-white left-1/2 space-x-2">
          <button
            onClick={toggleBooksTableVisibility}
            className="rounded bg-cyan-700 text-white px-6 py-2 font-semibold hover:bg-cyan-800"
          >
            {isBooksTableVisible
              ? 'Hide All Books'
              : 'Show All Books'}
          </button>
          <button
            onClick={() => setIsModalOpen(true)}
            className="rounded bg-green-700 text-white px-6 py-2 font-semibold hover:bg-green-800"
          >
            + Add Book
          </button>
        </div>
      </div>

      {/* Books Table */}
      {isBooksTableVisible && (
        <div className="lg:flex-wrap lg:flex md:flex-1 my-4">
          <table className="w-full border rounded overflow-hidden">
            <thead className="bg-gray-800 text-white">
              <tr>
                <th className="py-2 px-1">ID</th>
                <th className="py-2 px-1">Title</th>
                <th className="py-2 px-1">ISBN</th>
                <th className="py-2 px-1">Total Copies</th>
                <th className="py-2 px-1">Available Copies</th>
                <th className="py-2 px-1">Price</th>
                <th className="py-2 px-1">Actions</th>
              </tr>
            </thead>
            <tbody>
              {allBooks.map((book) => (
                <tr key={book.id} className="border-b text-center">
                  <td className="py-2 px-1">{book.id}</td>
                  <td className="py-2 px-1">{book.title}</td>
                  <td className="py-2 px-1">{book.isbn}</td>
                  <td className="py-2 px-1">{book.totalCopies}</td>
                  <td className="py-2 px-1">
                    {book.availableCopies}
                  </td>
                  <td className="py-2 px-1">
                    {book.price.toFixed(2)}€
                  </td>
                  <td className="py-2 px-1">
                    <div className="flex">
                      <button
                        onClick={() => openEditModal(book)}
                        className="rounded bg-blue-700 text-white px-3 py-2 mx-1 font-semibold hover:bg-blue-800"
                      >
                        Edit
                      </button>
                      <button
                        onClick={async () => {
                          await BookService.deleteBook(book.id);
                          refreshBooks();
                          alert('Book deleted successfully.');
                        }}
                        className="rounded bg-red-700 text-white px-3 py-2 mx-1 font-semibold hover:bg-red-800"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add Book Modal */}
      <Dialog
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        className="relative z-10"
      >
        <div className="fixed inset-0 bg-black bg-opacity-50" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="w-full max-w-md bg-white rounded-lg p-6">
            <Dialog.Title className="text-lg font-medium text-gray-900">
              Add New Book
            </Dialog.Title>
            <div className="mt-4">
              <label className="block text-gray-700">Title</label>
              <input
                type="text"
                className="w-full mt-2 p-2 border rounded"
                value={newBook.title}
                onChange={(e) =>
                  setNewBook({ ...newBook, title: e.target.value })
                }
              />
            </div>
            <div className="mt-4">
              <label className="block text-gray-700">ISBN</label>
              <input
                type="text"
                className="w-full mt-2 p-2 border rounded"
                value={newBook.isbn}
                onChange={(e) =>
                  setNewBook({ ...newBook, isbn: e.target.value })
                }
              />
            </div>
            <div className="mt-4">
              <label className="block text-gray-700">
                Total Copies
              </label>
              <input
                type="number"
                className="w-full mt-2 p-2 border rounded"
                value={newBook.totalCopies}
                onChange={(e) =>
                  setNewBook({
                    ...newBook,
                    totalCopies: parseInt(e.target.value, 10),
                  })
                }
              />
            </div>
            <div className="mt-4">
              <label className="block text-gray-700">Price</label>
              <input
                type="number"
                className="w-full mt-2 p-2 border rounded"
                value={newBook.price}
                onChange={(e) =>
                  setNewBook({
                    ...newBook,
                    price: parseFloat(e.target.value) || 0, // Ensure it's a valid number
                  })
                }
              />
            </div>
            <div className="mt-6 flex justify-end space-x-4">
              <button
                onClick={() => setIsModalOpen(false)}
                className="rounded bg-gray-500 text-white px-4 py-2 font-semibold hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={handleAddBook}
                className="rounded bg-green-700 text-white px-4 py-2 font-semibold hover:bg-green-800"
              >
                Save
              </button>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>

      {/* Edit Book Modal */}
      <Dialog
        open={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        className="relative z-10"
      >
        <div className="fixed inset-0 bg-black bg-opacity-50" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="w-full max-w-md bg-white rounded-lg p-6">
            <Dialog.Title className="text-lg font-medium text-gray-900">
              Edit Book
            </Dialog.Title>
            <div className="mt-4">
              <label className="block text-gray-700">Title</label>
              <input
                type="text"
                className="w-full mt-2 p-2 border rounded"
                value={editBook?.title || ''}
                onChange={(e) =>
                  setEditBook({ ...editBook, title: e.target.value })
                }
              />
            </div>
            <div className="mt-4">
              <label className="block text-gray-700">ISBN</label>
              <input
                type="text"
                className="w-full mt-2 p-2 border rounded"
                value={editBook?.isbn || ''}
                onChange={(e) =>
                  setEditBook({ ...editBook, isbn: e.target.value })
                }
              />
            </div>
            <div className="mt-4">
              <label className="block text-gray-700">
                Total Copies
              </label>
              <input
                type="number"
                className="w-full mt-2 p-2 border rounded"
                value={editBook?.totalCopies || 0}
                onChange={(e) =>
                  setEditBook({
                    ...editBook,
                    totalCopies: parseInt(e.target.value, 10),
                  })
                }
              />
            </div>
            <div className="mt-4">
              <label className="block text-gray-700">Price (€)</label>
              <input
                type="number"
                step="1.0"
                className="w-full mt-2 p-2 border rounded"
                value={editBook?.price || 0}
                onChange={(e) =>
                  setEditBook({
                    ...editBook,
                    price: parseFloat(e.target.value) || 0,
                  })
                }
              />
            </div>
            <div className="mt-4">
              <label className="block text-gray-700">Status</label>
              <select
                className="w-full mt-2 p-2 border rounded"
                value={editBook?.status || 'UPDATED'} // Ensure fallback to 'UPDATED'
                onChange={(e) => {
                  const newStatus = e.target.value || 'UPDATED';
                  setEditBook({
                    ...editBook,
                    status: newStatus,
                  });
                }}
              >
                <option value="UPDATED">Updated</option>
                <option value="ADDED">Added to Inventory</option>
                <option value="RETURNED">Returned</option>
                <option value="LOST">Lost</option>
              </select>
            </div>
            <div className="mt-6 flex justify-end space-x-4">
              <button
                onClick={() => setIsEditModalOpen(false)}
                className="rounded bg-gray-500 text-white px-4 py-2 font-semibold hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateBook}
                className="rounded bg-green-700 text-white px-4 py-2 font-semibold hover:bg-green-800"
              >
                Save
              </button>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>

      <hr className="w-960 h-2 mx-auto my-4 bg-cyan-800 border-0 rounded md:my-10 dark:bg-gray-700" />
      {/* Issue Books to Teachers Section */}
      <div>
        <div className="inline-flex flex-col items-center justify-center w-full">
          <h1 className="text-2xl text-center font-semibold text-cyan-900">
            Give Books to Teacher Service
          </h1>
          <h2
            className="text-lg text-center text-gray-600 mt-1 mb-2"
            dir="rtl"
          >
            خدمة إعارة الكتب للمعلمات
          </h2>
        </div>

        <div className="inline-flex items-center justify-center w-full">
          <button
            onClick={() =>
              setIsTeacherListVisible(!isTeacherListVisible)
            }
            className="rounded bg-cyan-700 text-white px-6 py-2 font-semibold hover:bg-cyan-800"
          >
            {isTeacherListVisible ? 'Hide List' : 'Show List'}
          </button>
        </div>
      </div>
      {/* Issue Books to Teachers Section */}
      <div className="max-w-3xl mx-auto rounded-lg p-4">
        {/* Teacher List */}
        {isTeacherListVisible && (
          <div className="max-h-64 overflow-y-auto">
            {allTeachers.map((teacher) => (
              <div
                key={teacher.id}
                className="flex items-center justify-between px-3 py-2 border-b last:border-none hover:bg-gray-50"
              >
                {/* Teacher Info */}
                <div className="text-sm text-gray-700 truncate">
                  <span className="font-medium">
                    {teacher.firstName} {teacher.lastName}
                  </span>
                  <span className="text-gray-500 ml-2">
                    ({teacher.classAbbrev})
                  </span>
                </div>

                {/* Action */}
                <button
                  onClick={() => openIssueBookModal(teacher)}
                  className="bg-blue-600 text-white px-3 py-1 text-sm rounded hover:bg-blue-700 transition"
                >
                  Give
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
      <hr className="w-960 h-2 mx-auto my-4 bg-cyan-800 border-0 rounded md:my-10 dark:bg-gray-700" />

      <div className="inline-flex flex-col items-center justify-center w-full">
        <h1 className="text-2xl text-center font-semibold text-cyan-900">
          Books Email Notification Service For Students Parents
        </h1>
        <h2
          className="text-lg text-center text-gray-600 mt-1 mb-2"
          dir="rtl"
        >
          خدمة إرسال إشعارات البريد الإلكتروني للكتب لأولياء الأمور
        </h2>
      </div>
      <div className="inline-flex items-center justify-center w-full">
        <button
          onClick={() => setIsBooksEmailVisible(!isBooksEmailVisible)}
          className="rounded bg-cyan-700 text-white px-6 py-2 font-semibold hover:bg-cyan-800"
        >
          {isBooksEmailVisible
            ? 'Hide Books Email Notification'
            : 'Show Books Email Notification'}
        </button>
      </div>

      {isBooksEmailVisible && (
        <>
          {loading ? (
            <p>Loading...</p>
          ) : (
            allTeachers.map((teacher) => (
              <TeacherClass
                key={teacher.id}
                teacher={teacher}
                books={allBooks}
                students={studentsByTeacher[teacher.id] || []}
              />
            ))
          )}
        </>
      )}

      <Dialog
        open={isIssueBookModalOpen}
        onClose={() => setIsIssueBookModalOpen(false)}
        className="relative z-10"
      >
        {/* Overlay */}
        <div className="fixed inset-0 bg-black/40" />

        <div className="fixed inset-0 flex items-center justify-center p-3">
          <Dialog.Panel className="w-full max-w-5xl max-h-[85vh] bg-white rounded-lg shadow-lg flex flex-col">
            {/* Header */}
            <div className="px-4 py-3 border-b">
              <Dialog.Title className="text-base font-semibold text-gray-800">
                Issue Books to{' '}
                <span className="text-blue-600">
                  {selectedTeacher?.firstName}{' '}
                  {selectedTeacher?.lastName}
                </span>
              </Dialog.Title>
            </div>

            {/* Book List */}
            <div className="flex-1 overflow-y-auto px-3 py-2 space-y-2">
              {allBooks.map((book) => {
                const isChecked = selectedBookIds.includes(book.id);

                return (
                  <div
                    key={book.id}
                    className="flex items-center justify-between gap-2 p-2 border rounded hover:bg-gray-50"
                  >
                    {/* Left: Book Info */}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-800 truncate">
                        {book.title}
                      </p>
                      <p className="text-xs text-gray-500">
                        Available: {book.availableCopies}
                      </p>
                    </div>

                    {/* Right: Controls */}
                    <div className="flex items-center gap-2">
                      {/* Quantity */}
                      <input
                        type="number"
                        min={0}
                        max={book.availableCopies}
                        disabled={!isChecked}
                        value={
                          isChecked
                            ? bookQuantities[book.id] || ''
                            : ''
                        }
                        onChange={(e) => {
                          setBookQuantities({
                            ...bookQuantities,
                            [book.id]: parseInt(e.target.value, 10),
                          });
                        }}
                        className="w-14 px-2 py-1 text-sm border rounded focus:ring-1 focus:ring-blue-400 disabled:bg-gray-100"
                      />

                      {/* Checkbox */}
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedBookIds([
                              ...selectedBookIds,
                              book.id,
                            ]);
                          } else {
                            setSelectedBookIds(
                              selectedBookIds.filter(
                                (id) => id !== book.id,
                              ),
                            );
                            setBookQuantities({
                              ...bookQuantities,
                              [book.id]: '',
                            });
                          }
                        }}
                        className="h-4 w-4 text-green-600 rounded focus:ring-1 focus:ring-green-500"
                      />
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Footer */}
            <div className="px-4 py-3 border-t flex justify-end gap-2">
              <button
                onClick={() => setIsIssueBookModalOpen(false)}
                className="bg-gray-500 text-white px-3 py-1.5 text-sm rounded hover:bg-gray-600 transition"
              >
                Cancel
              </button>

              <button
                onClick={handleIssueBooksToTeacher}
                className="bg-green-600 text-white px-3 py-1.5 text-sm rounded hover:bg-green-700 transition"
              >
                Issue
              </button>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>

      <hr className="w-960 h-2 mx-auto my-4 bg-cyan-800 border-0 rounded md:my-10 dark:bg-gray-700" />

      <div className="mx-auto text-center">
        <button
          onClick={() => {
            navigate('/schoolsuperadmin');
          }}
          className="rounded text-white px-6 py-2 font-semibold bg-cyan-700 hover:bg-cyan-800"
        >
          Back To Dashboard
        </button>
      </div>
    </div>
  );
};

export default BooksServices;
